import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import {
  CalendarDays,
  PlusCircle,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Users,
  AlertCircle,
  AlertTriangle,
  Heart,
} from "lucide-react";
import { parseISO } from "date-fns";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pendiente: { label: "Pendiente", className: "bg-amber-100 text-amber-700 border-amber-200" },
    aprobado: { label: "Aprobada", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    rechazado: { label: "Rechazada", className: "bg-red-100 text-red-700 border-red-200" },
    anulado: { label: "Anulada", className: "bg-slate-100 text-slate-700 border-slate-200" },
  };

  const config = statusConfig[status] || statusConfig.pendiente;

  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = "orange" }) => {
  const colorClasses = {
    orange: "bg-orange-50 text-orange-600",
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    slate: "bg-slate-100 text-slate-600",
  };

  return (
    <Card className="border-slate-100 hover:shadow-md transition-shadow duration-300">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-4xl font-bold text-slate-800">{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [misLlamadas, setMisLlamadas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, llamadasRes] = await Promise.all([
          api.getStatistics(),
          api.getLlamadas().catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setMisLlamadas(llamadasRes.data || []);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      // Usar parseISO para evitar el desfase por zona horaria UTC
      return parseISO(dateString).toLocaleDateString("es-GT", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const usagePercent = stats ? Math.min((stats.total_usado_historico / stats.total_acumulado_historico) * 100, 100) : 0;

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
            ¡Hola, {user?.nombre}!
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.puesto} • {user?.departamento}
          </p>
          {user?.supervisor_nombre && user.supervisor_nombre !== "N/A" && (
            <p className="text-sm text-slate-400 mt-1">
              Supervisor: {user.supervisor_nombre}
            </p>
          )}
        </div>
        <Button
          onClick={() => navigate("/nueva-solicitud")}
          className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20"
          data-testid="new-request-btn"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Nueva Solicitud
        </Button>
      </div>

      {/* Alerta de Expediente Disciplinario si tiene llamadas de atención */}
      {misLlamadas.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm animate-fadeIn">
          <div className="flex items-start sm:items-center gap-3">
            <div className="p-2.5 bg-amber-100 text-amber-700 rounded-xl shrink-0 mt-0.5 sm:mt-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-amber-900 text-sm">
                Tienes {misLlamadas.length} {misLlamadas.length === 1 ? "Llamada de Atención registrada" : "Llamadas de Atención registradas"} en tu perfil
              </h4>
              <p className="text-xs text-amber-700 mt-0.5">
                Puedes revisar el detalle de la retroalimentación, compromisos y descargar el acta formal en PDF.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => navigate("/llamadas-atencion")}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs shrink-0 self-end sm:self-center"
          >
            Ver en mi Apartado
          </Button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <StatCard
          title="Días Disponibles"
          value={stats?.dias_disponibles || 0}
          subtitle="disponibles hoy"
          icon={CalendarDays}
          color="orange"
        />
        <StatCard
          title="Acumulado Periodo"
          value={stats?.acumulado_anio_actual || 0}
          subtitle="año en curso"
          icon={TrendingUp}
          color="sky"
        />
        <StatCard
          title="Días Usados"
          value={stats?.total_usado_historico || 0}
          subtitle="histórico total"
          icon={CheckCircle}
          color="emerald"
        />
        <StatCard
          title="Días Totales"
          value={stats?.total_acumulado_historico || 0}
          subtitle="desde ingreso"
          icon={PlusCircle}
          color="slate"
        />
        {(user?.rol === "supervisor" || user?.rol === "administrador") && (
          <StatCard
            title="Pendientes"
            value={stats?.solicitudes_pendientes || 0}
            subtitle="por aprobar"
            icon={Clock}
            color="slate"
          />
        )}
      </div>

      {/* Balance Card */}
      <Card className="border-slate-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800">
            Balance de Vacaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-5xl font-bold text-slate-800">{stats?.dias_disponibles || 0}</p>
                <p className="text-slate-500">días disponibles</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">
                  {stats?.total_usado_historico || 0} usados de {stats?.total_acumulado_historico || 0} totales
                </p>
              </div>
            </div>
            <Progress value={usagePercent} className="h-3" />
            <p className="text-xs text-slate-400">
              Cálculo: 1.25 días por mes trabajado (15 días al año)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Requests */}
        <Card className="border-slate-100">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800">
              Mis Solicitudes Recientes
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/historial")}
              className="text-orange-600 hover:text-orange-700"
            >
              Ver todas
            </Button>
          </CardHeader>
          <CardContent>
            {stats?.solicitudes_recientes?.length > 0 ? (
              <div className="space-y-3">
                {stats.solicitudes_recientes.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                        <CalendarDays className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">
                          {formatDate(req.fecha_inicio)} - {formatDate(req.fecha_fin)}
                        </p>
                        <p className="text-xs text-slate-500">{req.dias_solicitados} días</p>
                      </div>
                    </div>
                    <StatusBadge status={req.estado} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tienes solicitudes recientes</p>
                <Button
                  variant="link"
                  onClick={() => navigate("/nueva-solicitud")}
                  className="text-orange-600 mt-2"
                >
                  Crear tu primera solicitud
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions / Team Overview */}
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800">
              {user?.rol === "empleado" ? "Acciones Rápidas" : "Resumen del Equipo"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {user?.rol === "empleado" ? (
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="h-24 flex flex-col items-center justify-center gap-2 hover:border-orange-200 hover:bg-orange-50"
                  onClick={() => navigate("/nueva-solicitud")}
                >
                  <PlusCircle className="w-6 h-6 text-orange-500" />
                  <span className="text-sm">Nueva Solicitud</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex flex-col items-center justify-center gap-2 hover:border-sky-200 hover:bg-sky-50"
                  onClick={() => navigate("/calendario")}
                >
                  <CalendarDays className="w-6 h-6 text-sky-500" />
                  <span className="text-sm">Ver Calendario</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex flex-col items-center justify-center gap-2 hover:border-emerald-200 hover:bg-emerald-50"
                  onClick={() => navigate("/historial")}
                >
                  <Clock className="w-6 h-6 text-emerald-500" />
                  <span className="text-sm">Historial</span>
                </Button>
                <div className="h-24 flex flex-col items-center justify-center gap-2 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                  <Users className="w-6 h-6 text-slate-400" />
                  <span className="text-sm text-slate-500">Mi Equipo</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Solicitudes Pendientes</p>
                      <p className="text-sm text-amber-600">Requieren tu atención</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-amber-700">
                    {stats?.solicitudes_pendientes || 0}
                  </span>
                </div>

                {stats?.total_equipo && (
                  <div className="flex items-center justify-between p-4 bg-sky-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-sky-600" />
                      <div>
                        <p className="font-medium text-sky-800">Colaboradores</p>
                        <p className="text-sm text-sky-600">Total activos</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-sky-700">
                      {stats.total_equipo}
                    </span>
                  </div>
                )}

                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => navigate("/aprobaciones")}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Revisar Solicitudes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
